<template>
    <div id="section3" class="history-section">
        <v-toolbar class="history-toolbar">
            <div class="history-toolbar-inner">
                <v-toolbar-title
                    class="history-toolbar-title subtitle-2"
                    @click="expanded = !expanded">
                    Istoric
                </v-toolbar-title>

                <div
                    v-if="expanded && $vuetify.display.mdAndUp"
                    class="history-toolbar-controls"
                    @click.stop>
                    <v-autocomplete
                        v-model="filters.product_code"
                        class="history-toolbar-field"
                        :items="productCodeSuggestions"
                        label="Cod produs"
                        variant="outlined"
                        density="compact"
                        clearable
                        hide-details="auto"
                        prepend-inner-icon="mdi-barcode"
                        @keyup.enter="reload"
                        @click:clear="reload"
                    />
                    <v-select
                        v-model="filters.change_type"
                        class="history-toolbar-field"
                        :items="changeTypeOptions"
                        label="Tip modificare"
                        variant="outlined"
                        density="compact"
                        clearable
                        hide-details="auto"
                    />
                    <v-autocomplete
                        v-model="filters.changed_by"
                        class="history-toolbar-field"
                        :items="changedBySuggestions"
                        item-title="title"
                        item-value="value"
                        label="Modificat de"
                        variant="outlined"
                        density="compact"
                        clearable
                        hide-details="auto"
                        @click:clear="reload"
                    />
                    <v-text-field
                        v-model="filters.date"
                        class="history-toolbar-field"
                        label="Data"
                        type="date"
                        variant="outlined"
                        density="compact"
                        clearable
                        hide-details="auto"
                        @click:clear="reload"
                    />
                    <div class="history-toolbar-actions">
                        <v-btn
                            color="primary"
                            variant="flat"
                            size="small"
                            :loading="loading"
                            @click="reload">
                            Filtrează
                        </v-btn>
                        <v-btn
                            icon
                            variant="text"
                            size="small"
                            :loading="loading"
                            @click="clearFiltersAndReload">
                            <v-icon>mdi-filter-off</v-icon>
                            <v-tooltip activator="parent" location="bottom">
                                Șterge filtrele
                            </v-tooltip>
                        </v-btn>
                        <v-btn
                            icon
                            variant="text"
                            size="small"
                            :loading="loading"
                            @click="reload">
                            <v-icon>mdi-refresh</v-icon>
                            <v-tooltip activator="parent" location="bottom">
                                Reîncarcă istoricul
                            </v-tooltip>
                        </v-btn>
                    </div>
                </div>

                <div
                    v-else-if="$vuetify.display.mdAndUp"
                    class="history-toolbar-controls">
                </div>

                <div class="history-toolbar-actions">
                    <v-btn
                        icon
                        variant="text"
                        :aria-label="expanded ? 'Restrânge istoricul' : 'Extinde istoricul'"
                        @click="expanded = !expanded">
                        <v-icon>
                            {{ expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                        </v-icon>
                    </v-btn>
                </div>
            </div>
        </v-toolbar>

        <v-expand-transition>
            <div v-if="expanded">
                <div
                    v-if="$vuetify.display.smAndDown"
                    class="history-filters-mobile px-3 py-3">
                    <v-row dense>
                        <v-col cols="12">
                            <v-autocomplete
                                v-model="filters.product_code"
                                :items="productCodeSuggestions"
                                label="Cod produs"
                                variant="outlined"
                                density="compact"
                                clearable
                                hide-details="auto"
                                prepend-inner-icon="mdi-barcode"
                                @keyup.enter="reload"
                                @click:clear="reload"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select
                                v-model="filters.change_type"
                                :items="changeTypeOptions"
                                label="Tip modificare"
                                variant="outlined"
                                density="compact"
                                clearable
                                hide-details="auto"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-autocomplete
                                v-model="filters.changed_by"
                                :items="changedBySuggestions"
                                item-title="title"
                                item-value="value"
                                label="Modificat de"
                                variant="outlined"
                                density="compact"
                                clearable
                                hide-details="auto"
                                @click:clear="reload"
                            />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field
                                v-model="filters.date"
                                label="Data"
                                type="date"
                                variant="outlined"
                                density="compact"
                                clearable
                                hide-details="auto"
                                @click:clear="reload"
                            />
                        </v-col>
                        <v-col cols="12" class="d-flex ga-2 flex-wrap">
                            <v-btn
                                color="primary"
                                variant="flat"
                                :loading="loading"
                                @click="reload">
                                Filtrează
                            </v-btn>
                            <v-btn
                                icon
                                variant="text"
                                :loading="loading"
                                @click="clearFiltersAndReload">
                                <v-icon>mdi-filter-off</v-icon>
                                <v-tooltip activator="parent" location="bottom">
                                    Șterge filtrele
                                </v-tooltip>
                            </v-btn>
                            <v-btn
                                icon
                                variant="text"
                                :loading="loading"
                                @click="reload">
                                <v-icon>mdi-refresh</v-icon>
                                <v-tooltip activator="parent" location="bottom">
                                    Reîncarcă istoricul
                                </v-tooltip>
                            </v-btn>
                        </v-col>
                    </v-row>
                </div>

                <v-data-table
                    :headers="headers"
                    :items="displayRows"
                    :loading="loading"
                    variant="outlined"
                    density="compact"
                    item-value="id"
                    :items-per-page="25"
                >
                    <template v-slot:[`item.product_code`]="{ item }">
                        <a
                            v-if="item.product_code"
                            href="#"
                            class="history-filter-link"
                            @click.prevent="filterByProductCode(item.product_code)">
                            {{ item.product_code }}
                        </a>
                        <span v-else class="text-medium-emphasis">—</span>
                    </template>
                    <template v-slot:[`item.field_path`]="{ item }">
                        <span class="text-body-2">{{ translateFieldPath(item.field_path) }}</span>
                    </template>
                    <template v-slot:[`item.old_value`]="{ item }">
                        <span class="history-value">{{ formatValue(item.old_value) }}</span>
                    </template>
                    <template v-slot:[`item.new_value`]="{ item }">
                        <span class="history-value">{{ formatValue(item.new_value) }}</span>
                    </template>
                    <template v-slot:[`item.change_type`]="{ item }">
                        {{ translateChangeType(item.change_type) }}
                    </template>
                    <template v-slot:[`item.changed_by_name`]="{ item }">
                        <a
                            v-if="item.changed_by_id"
                            href="#"
                            class="history-filter-link"
                            @click.prevent="filterByChangedBy(item)">
                            {{ item.changed_by_name || "Necunoscut" }}
                        </a>
                        <span v-else>{{ item.changed_by_name || "Necunoscut" }}</span>
                    </template>
                    <template v-slot:[`item.changed_at`]="{ item }">
                        <a
                            v-if="item.changed_at"
                            href="#"
                            class="history-filter-link"
                            @click.prevent="filterByDate(item.changed_at)">
                            {{ formatDateTime(item.changed_at) }}
                        </a>
                    </template>
                    <template v-slot:no-data>
                        <div class="text-center text-medium-emphasis py-6">
                            {{ emptyMessage }}
                        </div>
                    </template>
                </v-data-table>
            </div>
        </v-expand-transition>
    </div>
</template>

<script>
import { fetchPriceListHistory } from "@/utils/priceListApi";
import {
    formatPriceListFieldValue,
    humanizePriceListFieldPath,
} from "@/utils/priceListFieldLabels";

const CHANGE_TYPE_LABELS = {
    create: "Creare",
    update: "Actualizare",
    delete: "Ștergere",
};

function tryParseJson(value) {
    if (value === null || value === undefined || value === "") return null;
    if (typeof value === "object") return value;
    if (typeof value !== "string") return null;
    const trimmed = value.trim();
    if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) return null;
    try {
        return JSON.parse(trimmed);
    } catch {
        return null;
    }
}

function isEmptyContainer(value) {
    if (value === null || value === undefined || value === "") return true;
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "object") return Object.keys(value).length === 0;
    return false;
}

/** Expand a whole-object history row into leaf rows (for older create logs). */
function expandObjectDiff(basePath, oldVal, newVal, rows, template) {
    const left = oldVal === null || oldVal === undefined ? (Array.isArray(newVal) ? [] : {}) : oldVal;
    const right = newVal === null || newVal === undefined ? (Array.isArray(oldVal) ? [] : {}) : newVal;

    if (
        left !== null &&
        right !== null &&
        typeof left === "object" &&
        typeof right === "object" &&
        !Array.isArray(left) &&
        !Array.isArray(right)
    ) {
        const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
        for (const key of [...keys].sort()) {
            const childPath = basePath ? `${basePath}.${key}` : key;
            if (!(key in left)) {
                expandObjectDiff(childPath, null, right[key], rows, template);
            } else if (!(key in right)) {
                expandObjectDiff(childPath, left[key], null, rows, template);
            } else {
                expandObjectDiff(childPath, left[key], right[key], rows, template);
            }
        }
        return;
    }

    if (Array.isArray(left) && Array.isArray(right)) {
        const maxLen = Math.max(left.length, right.length);
        for (let index = 0; index < maxLen; index += 1) {
            const childPath = `${basePath}[${index}]`;
            if (index >= left.length) {
                expandObjectDiff(childPath, null, right[index], rows, template);
            } else if (index >= right.length) {
                expandObjectDiff(childPath, left[index], null, rows, template);
            } else {
                expandObjectDiff(childPath, left[index], right[index], rows, template);
            }
        }
        return;
    }

    if (isEmptyContainer(left) && isEmptyContainer(right)) {
        return;
    }
    if (left === right) {
        return;
    }

    rows.push({
        ...template,
        id: `${template.id}-${basePath}`,
        field_path: basePath,
        old_value: left === null || left === undefined ? "" : (typeof left === "object" ? JSON.stringify(left) : String(left)),
        new_value: right === null || right === undefined ? "" : (typeof right === "object" ? JSON.stringify(right) : String(right)),
    });
}

function expandHistoryEntry(entry) {
    const oldParsed = tryParseJson(entry.old_value);
    const newParsed = tryParseJson(entry.new_value);
    const looksLikeObject =
        (oldParsed && typeof oldParsed === "object") ||
        (newParsed && typeof newParsed === "object");

    if (!looksLikeObject) {
        if (
            entry.field_path === "category_assignments" &&
            (entry.new_value === "[]" || entry.new_value === "") &&
            (!entry.old_value || entry.old_value === "[]" || entry.old_value === "—")
        ) {
            return [];
        }
        return [entry];
    }

    const rows = [];
    expandObjectDiff(
        entry.field_path || "",
        oldParsed,
        newParsed,
        rows,
        entry,
    );
    return rows.length ? rows : [];
}

function emptyFilters() {
    return {
        product_code: null,
        change_type: null,
        changed_by: null,
        date: null,
    };
}

function toDateInputValue(dateTime) {
    if (!dateTime) return null;
    const date = new Date(dateTime);
    if (Number.isNaN(date.getTime())) {
        const match = String(dateTime).match(/^(\d{4}-\d{2}-\d{2})/);
        return match ? match[1] : null;
    }
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

export default {
    name: "PriceListHistoryLog",
    props: {
        priceListId: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            expanded: false,
            loading: false,
            entries: [],
            productCodeSuggestions: [],
            changedBySuggestions: [],
            filters: emptyFilters(),
            headers: [
                { title: "Cod produs", key: "product_code", sortable: true },
                { title: "Câmp", key: "field_path", sortable: true },
                { title: "Valoare veche", key: "old_value", sortable: false },
                { title: "Valoare nouă", key: "new_value", sortable: false },
                { title: "Tip", key: "change_type", sortable: true },
                { title: "Modificat de", key: "changed_by_name", sortable: true },
                { title: "Data", key: "changed_at", sortable: true },
            ],
            changeTypeOptions: [
                { title: "Creare", value: "create" },
                { title: "Actualizare", value: "update" },
                { title: "Ștergere", value: "delete" },
            ],
        };
    },
    computed: {
        displayRows() {
            const expanded = [];
            for (const entry of this.entries) {
                expanded.push(...expandHistoryEntry(entry));
            }
            return expanded;
        },
        emptyMessage() {
            if (!this.priceListId) {
                return "Selectați o listă de preț pentru a vedea istoricul.";
            }
            return "Nu există modificări înregistrate pentru această listă.";
        },
    },
    watch: {
        priceListId: {
            immediate: true,
            handler() {
                this.filters = emptyFilters();
                this.productCodeSuggestions = [];
                this.changedBySuggestions = [];
                this.reload();
            },
        },
    },
    methods: {
        rememberSuggestions(entries) {
            const codes = new Set(this.productCodeSuggestions);
            const usersById = new Map(
                this.changedBySuggestions.map((row) => [row.value, row]),
            );

            for (const entry of entries || []) {
                if (entry.product_code) {
                    codes.add(entry.product_code);
                }
                if (entry.changed_by_id) {
                    usersById.set(entry.changed_by_id, {
                        title: entry.changed_by_name || "Necunoscut",
                        value: entry.changed_by_id,
                    });
                }
            }

            this.productCodeSuggestions = [...codes].sort((a, b) =>
                String(a).localeCompare(String(b), "ro"),
            );
            this.changedBySuggestions = [...usersById.values()].sort((a, b) =>
                String(a.title).localeCompare(String(b.title), "ro"),
            );
        },
        async reload() {
            if (!this.priceListId) {
                this.entries = [];
                return;
            }
            this.loading = true;
            try {
                const params = { limit: 1000 };
                if (this.filters.product_code) {
                    params.product_code = String(this.filters.product_code).trim();
                }
                if (this.filters.change_type) {
                    params.change_type = this.filters.change_type;
                }
                if (this.filters.changed_by) {
                    params.changed_by = this.filters.changed_by;
                }
                if (this.filters.date) {
                    params.from = this.filters.date;
                    params.to = this.filters.date;
                }
                const data = await fetchPriceListHistory(this.priceListId, params);
                this.entries = data.entries || [];
                this.rememberSuggestions(this.entries);
            } catch (error) {
                console.error("Error fetching price list history:", error);
                this.entries = [];
            } finally {
                this.loading = false;
            }
        },
        clearFiltersAndReload() {
            this.filters = emptyFilters();
            this.reload();
        },
        filterByProductCode(productCode) {
            if (!productCode) return;
            this.filters.product_code = productCode;
            if (!this.productCodeSuggestions.includes(productCode)) {
                this.productCodeSuggestions = [...this.productCodeSuggestions, productCode]
                    .sort((a, b) => String(a).localeCompare(String(b), "ro"));
            }
            this.reload();
        },
        filterByChangedBy(item) {
            if (!item?.changed_by_id) return;
            this.filters.changed_by = item.changed_by_id;
            const existing = this.changedBySuggestions.find(
                (row) => row.value === item.changed_by_id,
            );
            if (!existing) {
                this.changedBySuggestions = [
                    ...this.changedBySuggestions,
                    {
                        title: item.changed_by_name || "Necunoscut",
                        value: item.changed_by_id,
                    },
                ].sort((a, b) => String(a.title).localeCompare(String(b.title), "ro"));
            }
            this.reload();
        },
        filterByDate(changedAt) {
            const dateValue = toDateInputValue(changedAt);
            if (!dateValue) return;
            this.filters.date = dateValue;
            this.reload();
        },
        translateFieldPath(path) {
            return humanizePriceListFieldPath(path);
        },
        translateChangeType(type) {
            return CHANGE_TYPE_LABELS[type] || type || "";
        },
        formatValue(value) {
            return formatPriceListFieldValue(value);
        },
        formatDateTime(dateTime) {
            if (!dateTime) return "";
            const date = new Date(dateTime);
            if (Number.isNaN(date.getTime())) return String(dateTime);
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, "0");
            const minutes = String(date.getMinutes()).padStart(2, "0");
            const seconds = String(date.getSeconds()).padStart(2, "0");
            return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
        },
    },
};
</script>

<style scoped>
.history-toolbar :deep(.v-toolbar__content) {
    height: auto !important;
    min-height: 64px;
    overflow: visible;
    padding-block: 8px;
    padding-inline: 16px;
}

.history-toolbar-inner {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 12px;
    width: 100%;
    min-width: 0;
}

.history-toolbar-title {
    flex: 0 0 auto !important;
    width: auto !important;
    max-width: none !important;
    margin-inline-end: 0 !important;
    padding-inline-end: 4px;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
}

.history-toolbar-controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex: 1 1 320px;
    min-width: 0;
}

.history-toolbar-field {
    flex: 1 1 150px;
    min-width: 140px;
    max-width: 240px;
}

.history-toolbar-field :deep(.v-field),
.history-toolbar-field :deep(.v-select__selection-text) {
    min-width: 0;
}

.history-toolbar-field :deep(.v-select__selection-text) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.history-toolbar-actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 4px;
}

.history-filter-link {
    color: rgb(var(--v-theme-primary));
    text-decoration: none;
    cursor: pointer;
}

.history-filter-link:hover {
    text-decoration: underline;
}

.history-value {
    display: inline-block;
    max-width: 220px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
}
</style>
